import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Button,
} from '@mui/material'
import { useFunctionalUnit } from '../../hooks/useFunctionalUnit'
import {
  CreateFunctionalUnitDTO,
  CareTypeEnum,
  FunctionalUnitDTO,
} from '../../services/functionalUnitService'
import {
  HospitalUnitType,
  MedicalUnitSubtype,
  MedicoTechnicalUnitSubtype,
  MedicoSocialUnitSubtype,
  AdministrativeUnitSubtype,
  TechnicalSupportUnitSubtype,
} from '../../services/dtos'
import { useEffect, useMemo, useState } from 'react'
import { findAll } from '../../state/thunks/siteThunk'
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks'
import { functionalUnitSchema } from '../../utils/schemas'

interface FunctionalUnitFormProps {
  onClose: () => void
  editingFunctionalUnit?: FunctionalUnitDTO | null
  onSuccess?: () => void
}

const FunctionalUnitForm = ({
  onClose,
  editingFunctionalUnit,
  onSuccess,
}: FunctionalUnitFormProps) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { create, update } = useFunctionalUnit()

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateFunctionalUnitDTO>({
    resolver: yupResolver(functionalUnitSchema),
    defaultValues: {
      name: editingFunctionalUnit?.name || '',
      number: editingFunctionalUnit?.number || '',
      siteId: editingFunctionalUnit?.siteId || 1,
      label: editingFunctionalUnit?.label || '',
      careType: editingFunctionalUnit?.careType || CareTypeEnum.Outpatient,
      isExecutingUnit: editingFunctionalUnit?.isExecutingUnit ?? false,
      isRequestingUnit: editingFunctionalUnit?.isRequestingUnit ?? false,
      functionType: editingFunctionalUnit?.functionType,
      functionSubType: editingFunctionalUnit?.functionSubType,
      serviceManagerName: editingFunctionalUnit?.serviceManagerName || '',
      details: editingFunctionalUnit?.details
        ? ({
            authorizationDate: editingFunctionalUnit.details.authorizationDate
              ? new Date(editingFunctionalUnit.details.authorizationDate)
                  .toISOString()
                  .split('T')[0]
              : undefined,
            authorizationEndDate: editingFunctionalUnit.details
              .authorizationEndDate
              ? new Date(editingFunctionalUnit.details.authorizationEndDate)
                  .toISOString()
                  .split('T')[0]
              : undefined,
            authorizationNumber:
              editingFunctionalUnit.details.authorizationNumber ?? '',
            authorizationType:
              editingFunctionalUnit.details.authorizationType ?? '',
            dedicatedBedsCount:
              editingFunctionalUnit.details.dedicatedBedsCount ?? 0,
            dedicatedBedType:
              editingFunctionalUnit.details.dedicatedBedType ?? '',
            hospitalizationMode:
              editingFunctionalUnit.details.hospitalizationMode ?? null,
          } as any)
        : ({
            authorizationDate: undefined,
            authorizationEndDate: undefined,
            authorizationNumber: '',
            authorizationType: '',
            dedicatedBedsCount: 0,
            dedicatedBedType: '',
            hospitalizationMode: undefined,
          } as any),
    },
  })

  const watchedCareType = watch('careType')
  const watchedFunctionType = watch('functionType')

  const sites = useAppSelector((state) => state.site.sites.datas)

  const functionSubTypeOptions = useMemo(() => {
    switch (watchedFunctionType) {
      case HospitalUnitType.MEDICAL:
        return Object.entries(MedicalUnitSubtype).map(([key, value]) => ({
          key,
          value,
        }))
      case HospitalUnitType.MEDICO_TECHNICAL:
        return Object.entries(MedicoTechnicalUnitSubtype).map(
          ([key, value]) => ({
            key,
            value,
          }),
        )
      case HospitalUnitType.MEDICO_SOCIAL:
        return Object.entries(MedicoSocialUnitSubtype).map(([key, value]) => ({
          key,
          value,
        }))
      case HospitalUnitType.ADMINISTRATIVE:
        return Object.entries(AdministrativeUnitSubtype).map(
          ([key, value]) => ({
            key,
            value,
          }),
        )
      case HospitalUnitType.TECHNICAL_SUPPORT:
        return Object.entries(TechnicalSupportUnitSubtype).map(
          ([key, value]) => ({
            key,
            value,
          }),
        )
      default:
        return []
    }
  }, [watchedFunctionType])

  useEffect(() => {
    if (watchedFunctionType && functionSubTypeOptions.length > 0) {
      const currentSubType = watch('functionSubType')
      const validSubTypes = functionSubTypeOptions.map((option) => option.value)
      if (currentSubType && !validSubTypes.includes(currentSubType)) {
        setValue('functionSubType', undefined)
      }
    } else if (!watchedFunctionType) {
      setValue('functionSubType', null)
    }
  }, [watchedFunctionType, functionSubTypeOptions, watch, setValue])

  useEffect(() => {
    const dedicatedBedsCount = watch('details.dedicatedBedsCount')
    if (dedicatedBedsCount === 0) {
      setValue('details.dedicatedBedType', undefined)
    }
  }, [watch('details.dedicatedBedsCount'), watch, setValue])

  useEffect(() => {
    if (
      watchedFunctionType &&
      watchedFunctionType !== HospitalUnitType.MEDICAL
    ) {
      setValue('details', null)
    }
  }, [watchedFunctionType, setValue])

  const onSubmit = async (data: CreateFunctionalUnitDTO) => {
    try {
      setIsSubmitting(true)

      // Create a clean copy of the data to avoid mutation
      const submitData = { ...data }

      if (submitData.details === null) {
        submitData.details = undefined
      }
      if (editingFunctionalUnit) {
        await update(editingFunctionalUnit.id, submitData)
      } else {
        await create(submitData)
      }
      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    dispatch(findAll())
  }, [dispatch])

  const careTypeOptions = [
    { value: CareTypeEnum.Outpatient, label: 'Patient Externe' },
    { value: CareTypeEnum.Inpatient, label: 'Hospitalisation' },
    { value: CareTypeEnum.Ambulatory, label: 'Ambulatoire' },
  ]

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <h2 className="text-2xl font-bold mb-4">
            {editingFunctionalUnit ? 'Modifier' : 'Ajouter'} une unité
            fonctionnelle
          </h2>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nom"
            {...register('name')}
            error={!!errors.name}
            helperText={errors.name?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Numéro"
            {...register('number')}
            error={!!errors.number}
            helperText={errors.number?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.siteId}>
            <InputLabel>Site</InputLabel>
            <Select
              value={watch('siteId')}
              label="Site"
              onChange={(e) => setValue('siteId', e.target.value as number)}
            >
              {sites?.map((site) => (
                <MenuItem key={site.id} value={site.id}>
                  {site.label}
                </MenuItem>
              ))}
            </Select>
            {errors.siteId && (
              <FormHelperText>{errors.siteId.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Libellé"
            {...register('label')}
            error={!!errors.label}
            helperText={errors.label?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Responsable de service"
            {...register('serviceManagerName')}
            error={!!errors.serviceManagerName}
            helperText={errors.serviceManagerName?.message}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.careType}>
            <InputLabel>Type de soins</InputLabel>
            <Select
              value={watchedCareType}
              label="Type de soins"
              onChange={(e) =>
                setValue('careType', e.target.value as CareTypeEnum)
              }
            >
              {careTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {errors.careType && (
              <FormHelperText>{errors.careType.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.functionType}>
            <InputLabel>Type d&apos;unité</InputLabel>
            <Select
              value={watchedFunctionType || ''}
              label="Type d'unité"
              onChange={(e) =>
                setValue(
                  'functionType',
                  e.target.value === ''
                    ? undefined
                    : (e.target.value as HospitalUnitType),
                )
              }
            >
              <MenuItem value="">
                <em>Sélectionner un type d&apos;unité</em>
              </MenuItem>
              {Object.entries(HospitalUnitType).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            {errors.functionType && (
              <FormHelperText>{errors.functionType.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth error={!!errors.functionSubType}>
            <InputLabel>Sous-type d&apos;unité</InputLabel>
            <Select
              value={watch('functionSubType') || ''}
              label="Sous-type d'unité"
              onChange={(e) =>
                setValue(
                  'functionSubType',
                  e.target.value === ''
                    ? undefined
                    : (e.target.value as string),
                )
              }
              disabled={
                !watchedFunctionType || functionSubTypeOptions.length === 0
              }
            >
              <MenuItem value="">
                <em>
                  {!watchedFunctionType
                    ? 'Sélectionner d\'abord un type d\'unité'
                    : 'Sélectionner un sous-type'}
                </em>
              </MenuItem>
              {functionSubTypeOptions.map((option) => (
                <MenuItem key={option.key} value={option.value}>
                  {option.value}
                </MenuItem>
              ))}
            </Select>
            {errors.functionSubType && (
              <FormHelperText>{errors.functionSubType.message}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={watch('isExecutingUnit')}
                onChange={(e) => setValue('isExecutingUnit', e.target.checked)}
              />
            }
            label="Unité d'exécution"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={watch('isRequestingUnit')}
                onChange={(e) => setValue('isRequestingUnit', e.target.checked)}
              />
            }
            label="Unité de demande"
          />
        </Grid>

        {watchedFunctionType === HospitalUnitType.MEDICAL && (
          <>
            <Grid item xs={12}>
              <h3 className="text-lg font-semibold mt-4 mb-2">
                Détails de l&apos;unité médicale
              </h3>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date d'autorisation"
                type="date"
                {...register('details.authorizationDate')}
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'authorizationDate' in errors.details &&
                    errors.details.authorizationDate
                  )
                }
                helperText={
                  errors.details &&
                  typeof errors.details === 'object' &&
                  'authorizationDate' in errors.details
                    ? errors.details.authorizationDate?.message
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date de fin d'autorisation"
                type="date"
                {...register('details.authorizationEndDate')}
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'authorizationEndDate' in errors.details &&
                    errors.details.authorizationEndDate
                  )
                }
                helperText={
                  errors.details &&
                  typeof errors.details === 'object' &&
                  'authorizationEndDate' in errors.details
                    ? errors.details.authorizationEndDate?.message
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Numéro d'autorisation"
                {...register('details.authorizationNumber')}
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'authorizationNumber' in errors.details &&
                    errors.details.authorizationNumber
                  )
                }
                helperText={
                  errors.details &&
                  typeof errors.details === 'object' &&
                  'authorizationNumber' in errors.details
                    ? errors.details.authorizationNumber?.message
                    : undefined
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Type d'autorisation"
                {...register('details.authorizationType')}
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'authorizationType' in errors.details &&
                    errors.details.authorizationType
                  )
                }
                helperText={
                  errors.details &&
                  typeof errors.details === 'object' &&
                  'authorizationType' in errors.details
                    ? errors.details.authorizationType?.message
                    : undefined
                }
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de lits dédiés"
                type="number"
                {...register('details.dedicatedBedsCount')}
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'dedicatedBedsCount' in errors.details &&
                    errors.details.dedicatedBedsCount
                  )
                }
                helperText={
                  errors.details &&
                  typeof errors.details === 'object' &&
                  'dedicatedBedsCount' in errors.details
                    ? errors.details.dedicatedBedsCount?.message
                    : undefined
                }
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'dedicatedBedType' in errors.details &&
                    errors.details.dedicatedBedType
                  )
                }
              >
                <InputLabel>Type de lits dédiés</InputLabel>
                <Select
                  value={watch('details.dedicatedBedType') || ''}
                  label="Type de lits dédiés"
                  onChange={(e) =>
                    setValue(
                      'details.dedicatedBedType',
                      e.target.value === ''
                        ? undefined
                        : (e.target.value as string),
                    )
                  }
                  disabled={watch('details.dedicatedBedsCount') === 0}
                >
                  <MenuItem value="">
                    <em>Sélectionner un type de lit</em>
                  </MenuItem>
                  <MenuItem value="Intensive Care">Soins Intensifs</MenuItem>
                  <MenuItem value="Palliative Care">Soins Palliatifs</MenuItem>
                  <MenuItem value="Pediatric">Pédiatrie</MenuItem>
                  <MenuItem value="Maternity">Maternité</MenuItem>
                  <MenuItem value="Psychiatric">Psychiatrie</MenuItem>
                  <MenuItem value="Isolation">Isolement</MenuItem>
                  <MenuItem value="Cardiac Care">Cardiologie</MenuItem>
                  <MenuItem value="Surgical Recovery">
                    Récupération Chirurgicale
                  </MenuItem>
                </Select>
                {errors.details &&
                  typeof errors.details === 'object' &&
                  'dedicatedBedType' in errors.details &&
                  errors.details.dedicatedBedType && (
                    <FormHelperText>
                      {errors.details.dedicatedBedType?.message}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl
                fullWidth
                error={
                  !!(
                    errors.details &&
                    typeof errors.details === 'object' &&
                    'hospitalizationMode' in errors.details &&
                    errors.details.hospitalizationMode
                  )
                }
              >
                <InputLabel>Mode d&apos;hospitalisation</InputLabel>
                <Select
                  value={watch('details.hospitalizationMode') || ''}
                  label="Mode d'hospitalisation"
                  onChange={(e) =>
                    setValue(
                      'details.hospitalizationMode',
                      e.target.value === ''
                        ? undefined
                        : (e.target.value as 'MIXED' | 'COMPLETE' | 'PARTIAL'),
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Sélectionner un mode</em>
                  </MenuItem>
                  <MenuItem value="MIXED">Mixte</MenuItem>
                  <MenuItem value="COMPLETE">Complète</MenuItem>
                  <MenuItem value="PARTIAL">Partielle</MenuItem>
                </Select>
                {errors.details &&
                  typeof errors.details === 'object' &&
                  'hospitalizationMode' in errors.details &&
                  errors.details.hospitalizationMode && (
                    <FormHelperText>
                      {errors.details.hospitalizationMode?.message}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>
          </>
        )}

        <Grid
          item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Annuler
          </Button>
          <Button variant="contained" type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? 'En cours...'
              : editingFunctionalUnit
              ? 'Sauvegarder'
              : 'Ajouter'}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}

export default FunctionalUnitForm
